<template>
  <SectionTabHoriz02Logo02
    v-if="contextData"
    :title="contextData.title"
    :description="contextData.description"
    :tabs="contextTabs"
    :theme="theme"
  />
</template>

<script setup lang="ts">
  import type { ISuiteProps } from '@/components/Shared/Suite/type.ts';

  interface IItem {
    name: string;
    description: string;
    link: string;
    linkName: string;
    thumbnail: string;
    wistiaCode?: string;
    trackingCode?: string;
    logos: string[];
    tag?: string;
  }

  interface IHardwaresTabsData {
    title: string;
    description?: string;
    tabs: {
      tabName: string;
      items: IItem[];
    }[];
  }

  interface IHardwaresTabs {
    component: 'hardwares-tabs';
    meta: {
      navigable?: string;
    };
    data: IHardwaresTabsData;
  }

  type IProps = ISuiteProps<IHardwaresTabs>;

  const props = defineProps<IProps>();

  const solutionsStore = useSolutionsStore();
  const { themeStyle } = storeToRefs(solutionsStore);

  const { provideFormatUrl } = useFormatUrl();
  const { provideSuiteThumbnailUrl, provideSuiteLogoUrl } = useImagePath(
    props.urlConnector
  );

  const contextData = computed((): IHardwaresTabsData => props.suiteData.data);

  const contextTabs = computed((): IHardwaresTabsData['tabs'] =>
    contextData.value.tabs.map((tabItem) => ({
      ...tabItem,
      items: tabItem.items.map(formatCarouselItem),
    }))
  );

  const theme = computed((): TTheme => {
    switch (themeStyle.value) {
      case 'sustainable':
        return 'green';
      case 'connected':
        return 'cyan';
      case 'ai':
        return 'purple';
      case 'operations':
        return 'yellow';
      default:
        return 'blue';
    }
  });

  const formatCarouselItem = (item: IItem): IItem => ({
    ...item,
    thumbnail: provideSuiteThumbnailUrl(item.thumbnail),
    link: provideFormatUrl(item.link),
    logos: item.logos.map(provideSuiteLogoUrl),
  });

  export type { IHardwaresTabs, IHardwaresTabsData, IItem };
</script>
